import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmployeePicker from "@/components/EmployeePicker";
import DatePicker from "@/components/DatePicker";
import ConfirmDialog from "@/components/ConfirmDialog";
import { SlipActions } from "@/components/advance/SlipActions";
import { useAuth } from "@/context/AuthContext";
import { isoToDmy } from "@/components/DatePicker";
import { api, errMsg, errCode, newKey } from "@/lib/api";
import { inr, todayISO, toPaise } from "@/lib/format";
import { toast } from "sonner";

export const PURPOSES = ["Purchase", "Cash Advance"];
const blank = () => ({ amount: "", date: todayISO(), purpose: "" });

function SavedSlip({ slip, onClose }) {
  return (
    <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 fade-up" data-testid="advance-saved-slip">
      <div className="text-sm">
        <div className="font-semibold text-emerald-800">Advance saved. Slip ready to print.</div>
        <div className="text-slate-700 mt-0.5" data-testid="advance-saved-slip-details"><b className="money">{slip.code}</b> {slip.name} · {slip.purpose} · <b className="money">{inr(slip.amount)}</b> · <span className="whitespace-nowrap">{isoToDmy(slip.date)}</span></div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SlipActions ids={[slip.id]} prefix="advance-slip" />
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="advance-saved-slip-close">Dismiss</Button>
      </div>
    </div>
  );
}

export default function ManualAdvance({ disabled, onSaved }) {
  const { user } = useAuth();
  const [slip, setSlip] = useState(null);
  const [emp, setEmp] = useState(null);
  const [f, setF] = useState(blank());
  const [key, setKey] = useState(newKey());
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const paise = toPaise(f.amount);
  const amtErr = f.amount !== "" && (!(paise > 0) || !/^\s*(\d+(\.\d{1,2})?|\.\d{1,2})\s*$/.test(f.amount.replace(/,/g, ""))) ? "Enter an amount greater than ₹0 (max 2 decimals)" : "";
  const valid = emp && paise > 0 && !amtErr && f.date && f.purpose;

  const save = async (allowDuplicate = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/advances", { employee_code: emp.code, date: f.date, amount: f.amount, purpose: f.purpose, idempotency_key: key, allow_duplicate: allowDuplicate });
      toast.success(`${f.purpose} advance of ${inr(paise / 100)} saved for ${emp.name}.${data.balance_after != null ? ` Outstanding: ${inr(data.balance_after)}` : ""}`);
      setDup(null); setEmp(null); setF(blank()); setKey(newKey()); setSlip(data.slip); onSaved?.();
    } catch (e) {
      if (errCode(e) === "DUPLICATE_ADVANCE") setDup(errMsg(e));
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl">
    {slip && <SavedSlip slip={slip} onClose={() => setSlip(null)} />}
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="manual-advance-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Employee ID *</Label><div className={`mt-1.5 ${disabled ? "opacity-60 pointer-events-none" : ""}`}><EmployeePicker value={emp} onChange={setEmp} source="/advances/employees" hideBalance testid="advance-employee-picker" placeholder="Search by Employee ID or name…" /></div></div>
        <div><Label>Employee Name</Label><Input className="mt-1.5 h-12 bg-slate-50" value={emp?.name || ""} readOnly placeholder="Filled automatically" data-testid="advance-employee-name" /></div>
        <div><Label>Department</Label><Input className="mt-1.5 h-11 bg-slate-50" value={emp ? emp.department || "-" : ""} readOnly placeholder="Filled automatically" data-testid="advance-employee-department" /></div>
        <div><Label>Branch</Label><Input className="mt-1.5 h-11 bg-slate-50" value={emp ? emp.branch || "-" : ""} readOnly placeholder="Filled automatically" data-testid="advance-employee-branch" /></div>
        <div><Label>Date *</Label><DatePicker max={todayISO()} className="mt-1.5 h-11" value={f.date} onChange={(v) => setF({ ...f, date: v })} disabled={disabled} testid="advance-date-input" /></div>
        <div>
          <Label>Advance Amount (₹) *</Label>
          <Input inputMode="decimal" className={`mt-1.5 h-11 money text-lg ${amtErr ? "border-red-500" : ""}`} placeholder="0.00" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} disabled={disabled} data-testid="advance-amount-input" />
          {amtErr && <div className="text-xs text-red-700 mt-1" data-testid="advance-amount-error">{amtErr}</div>}
        </div>
        <div>
          <Label>Purpose *</Label>
          <Select value={f.purpose} onValueChange={(v) => setF({ ...f, purpose: v })} disabled={disabled}>
            <SelectTrigger className="mt-1.5 h-11" data-testid="advance-purpose-select"><SelectValue placeholder="Select purpose" /></SelectTrigger>
            <SelectContent>{PURPOSES.map((p) => <SelectItem key={p} value={p} data-testid={`advance-purpose-${p.replace(" ", "-").toLowerCase()}`}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <p className="text-xs text-slate-500">Entered by <b data-testid="advance-entered-by">{user?.name}</b>. The advance is added to the employee's outstanding balance and a thermal slip is generated after saving. Nothing is deducted from salary until you enter a deduction yourself.</p>
      <Button className="h-11 px-6 bg-blue-800 hover:bg-blue-900" disabled={disabled || busy || !valid} onClick={() => save(false)} data-testid="add-advance-btn">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add Advance
      </Button>
      <ConfirmDialog open={!!dup} onOpenChange={(o) => !o && setDup(null)} busy={busy} tone="warning" testid="advance-duplicate-dialog" confirmText="Save anyway"
        title="Possible duplicate advance" description={dup} onConfirm={() => save(true)} />
    </div>
    </div>
  );
}
